import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import LeadCaptureDialog from "@/components/LeadCaptureDialog";
import {
  ArrowRight,
  ArrowUpRight,
  ShoppingBag,
  Tent,
  Wine,
  Coffee,
  WifiOff,
  ShieldCheck,
  Database,
  Mail,
  Zap,
  UploadCloud,
  QrCode,
  MonitorSmartphone,
  TrendingUp,
  Users,
  Package,
  Activity,
  Check,
  Sparkles,
  Building2,
  Boxes,
  MessageSquareQuote,
  Tv2,
  Hand,
  Wand2,
} from "lucide-react";

// ─── Tokens ────────────────────────────────────────────────────────────────
// Light-canvas era: warm blush+peach gradients with dark navy type.
const NAVY      = "#1B1340"; // type + occasional ink accents
const SAGE      = "#FF4D6D"; // remapped to coral — the brand accent
const SAGE_DARK = "#E03A5A"; // deeper coral for emphasis
const CREAM     = "#FFF6EB";
const BLUSH     = "#FFE4D6";
const PEACH     = "#FFD3B6";
const PANEL     = "#FFF1E4"; // soft panel
const INK       = NAVY;       // legacy alias used widely as type/border color
const SLATE     = "#3F2D5C";  // slightly tinted dark text

// Section backgrounds — soft gradients
const BG_HERO       = "linear-gradient(180deg, #FFFAF3 0%, #FFEED9 60%, #FFDDBF 100%)";
const BG_WARM_SOFT  = "linear-gradient(180deg, #FFF1E4 0%, #FFE2CE 100%)";
const BG_BLUSH_SOFT = "linear-gradient(180deg, #FFE4D6 0%, #FFC8DC 100%)";
const BG_VIOLET_SOFT= "linear-gradient(180deg, #EFE5FF 0%, #DFD0FF 100%)";
const BG_CREAM      = "linear-gradient(180deg, #FFF6EB 0%, #FFEFDB 100%)";
const BG_CYAN_SOFT  = "linear-gradient(180deg, #DDF3FF 0%, #BFE7FF 100%)";

// Pop palette — saturated accents
const POP = {
  coral:    "#FF4D6D",
  amber:    "#FFC233",
  violet:   "#8B5CF6",
  cyan:     "#22C5FF",
  lime:     "#14F195",
  lavender: "#8B5CF6",
  sky:      "#22C5FF",
  mint:     "#14F195",
};
const MUTED = "#5E6660";

// ─── Vertical Tabs Data ────────────────────────────────────────────────────
const VERTICALS = [
  {
    id: "furniture",
    title: "Furniture & Home Decor",
    eyebrow: "01",
    Icon: ShoppingBag,
    painLabel: "In-Store Pain Point",
    painText:
      "Shoppers walk into a 200-piece showroom, feel overwhelmed by the choice, and leave saying they'll \"think about it.\"",
    solutionLabel: "Getcurate Solution",
    solutionText:
      "An iPad by the door. Shopper answers a few questions about their room and taste — you show them the exact 3 pieces that fit, and which of your stores has them.",
    metric: "+28%",
    metricSub: "More in-store leads captured",
  },
  {
    id: "fashion",
    title: "Fashion, Boutique & Ethnic wear",
    eyebrow: "02",
    Icon: Tent,
    painLabel: "Retail Pain Point",
    painText:
      "Walk-ins ask your busy sales staff \"do you have something like…\" — and half the time you lose the sale because the staff can't remember what's in stock across sizes and stores.",
    solutionLabel: "Getcurate Solution",
    solutionText:
      "Your customer self-picks style, fit, occasion. AI matches them to the exact SKU + tells them which of your outlets stocks it. Works even when the store Wi-Fi is bad.",
    metric: "0",
    metricSub: "Sales lost to \"we don't have it\"",
  },
  {
    id: "wellness",
    title: "Wellness, Skincare & Ayurveda",
    eyebrow: "03",
    Icon: Wine,
    painLabel: "Counter Pain Point",
    painText:
      "Customers get confused between 40 SKUs — dry vs oily, kapha vs vata, sensitive vs acne. Your staff isn't a trained consultant on every product.",
    solutionLabel: "Getcurate Solution",
    solutionText:
      "A 60-second self-diagnosis on the counter tablet. Customer answers 3–4 questions about their skin/dosha, sees the exact regimen you'd have recommended anyway.",
    metric: "+35%",
    metricSub: "Higher basket size",
  },
  {
    id: "d2c",
    title: "D2C brands with 2–20 outlets",
    eyebrow: "04",
    Icon: Coffee,
    painLabel: "Growth Pain Point",
    painText:
      "You already run a Shopify store. But when a customer walks into your physical outlet, they get zero of the online guidance — they're on their own.",
    solutionLabel: "Getcurate Solution",
    solutionText:
      "One quiz. One QR code. Works at the store on iPad, on the website, and inside your newsletter. Same catalogue, same brand look. No engineer needed.",
    metric: "↓",
    metricSub: "Fewer walk-outs without buying",
  },
];

const INTEGRATIONS = [
  {
    name: "Shopify & WooCommerce",
    Icon: ShoppingBag,
    blurb: "Upload your catalogue as a CSV — we handle the images and stock mapping.",
    tag: "Commerce",
  },
  {
    name: "Mailer / Newsletter",
    Icon: Mail,
    blurb: "Embed the quiz link in Klaviyo, Mailchimp or your own newsletter. Same experience.",
    tag: "Email",
  },
  {
    name: "Any device, any store",
    Icon: Zap,
    blurb: "iPad, Android tablet, kiosk, phone. Works even when the shop Wi-Fi is patchy.",
    tag: "Anywhere",
  },
];

// ─── Page ──────────────────────────────────────────────────────────────────
export default function Landing() {
  const [leadOpen, setLeadOpen] = useState(false);
  const openLead = () => setLeadOpen(true);
  return (
    <div style={{ background: BG_CREAM, color: NAVY }} className="min-h-screen">
      <Header onBookDemo={openLead} />
      <Hero onBookDemo={openLead} />
      <FourStepsToLaunch />
      <CustomerKioskFlow />
      <ProblemBlock />
      <VerticalsSection />
      <IntegrationsSection />
      <ProductShowcase />
      <PricingSection onBookDemo={openLead} />
      <FinalCTA onBookDemo={openLead} />
      <Footer />
      <LeadCaptureDialog open={leadOpen} onOpenChange={setLeadOpen} source="landing" />
    </div>
  );
}

// ─── Header ────────────────────────────────────────────────────────────────
function Header({ onBookDemo }) {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md" style={{ background: "rgba(255,246,235,0.88)", borderBottom: "1px solid rgba(27,19,64,0.08)" }}>
      <div className="max-w-7xl mx-auto px-8 h-18 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <CurateMark />
          <span className="font-display font-bold tracking-tight text-lg" style={{ color: NAVY }}>Getcurate</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#1B1340]/45 hidden sm:inline">getcurate.shop</span>
        </Link>
        <nav className="hidden md:flex items-center gap-9 text-sm text-[#1B1340]/75">
          <a href="#verticals" className="hover:text-[#1B1340] transition-colors" data-testid="nav-use-cases">Use Cases</a>
          <a href="#integrations" className="hover:text-[#1B1340] transition-colors" data-testid="nav-integrations">Integrations</a>
          <a href="#pricing" className="hover:text-[#1B1340] transition-colors" data-testid="nav-pricing">Pricing</a>
          <Link to="/login" className="hover:text-[#1B1340] transition-colors" data-testid="nav-signin">Sign in</Link>
        </nav>
        <button
          onClick={onBookDemo}
          data-testid="nav-book-demo"
          className="px-5 h-10 text-sm font-medium rounded-full transition-all hover:scale-[1.02] shadow-[0_8px_18px_-8px_rgba(255,77,109,0.55)]"
          style={{ background: POP.coral, color: "white" }}
        >
          Book 10-Min Demo
        </button>
      </div>
    </header>
  );
}

function CurateMark() {
  return (
    <div className="relative w-8 h-8 flex items-center justify-center" style={{ background: SAGE }}>
      <span className="font-display font-black text-[#0F1210] text-sm tracking-tighter">C</span>
      <span className="absolute -bottom-1 -right-1 w-2 h-2 bg-white" />
    </div>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────────
function Hero({ onBookDemo }) {
  return (
    <section className="relative overflow-hidden" style={{ background: BG_HERO }}>
      {/* soft accent shapes — subtle, editorial */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full opacity-25" style={{ background: POP.amber }} />
        <div className="absolute top-1/3 -left-24 w-72 h-72 rounded-full opacity-20" style={{ background: POP.coral }} />
        <div className="absolute bottom-10 right-1/4 w-56 h-56 rounded-full opacity-15" style={{ background: POP.violet }} />
        <div className="absolute top-12 left-1/3 w-40 h-40 rounded-full opacity-20" style={{ background: POP.cyan }} />
      </div>
      <div className="relative max-w-7xl mx-auto px-8 pt-24 pb-32 lg:pt-32 lg:pb-40">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex items-center gap-3 mb-8" data-testid="hero-eyebrow">
            <span className="w-8 h-px" style={{ background: POP.coral }} />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#1B1340]/65">
              For brands with stores + a website
            </span>
          </div>
          <h1 className="font-display font-medium tracking-[-0.04em] text-5xl sm:text-6xl lg:text-[88px] leading-[0.95] max-w-5xl" style={{ color: NAVY }}>
            Every walk-in shopper<br />
            gets shown{" "}
            <span style={{ color: POP.coral, fontStyle: "italic", fontWeight: 500 }}>the right product</span>.
          </h1>
          <p className="mt-10 max-w-2xl text-[#1B1340]/75 text-lg leading-relaxed">
            Put an iPad in your store. Shopper taps through 3–4 simple questions.
            Getcurate&apos;s AI shows them the exact piece from your catalogue that suits them —
            with the price, and which of your stores has it in stock. Same magic works online,
            in your email, or shared as a QR code. No coding. Set up in 15 minutes.
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-6">
            <button
              onClick={onBookDemo}
              data-testid="hero-cta-demo"
              className="group inline-flex items-center gap-3 h-13 px-7 py-3.5 rounded-full font-medium text-sm transition-all hover:scale-[1.02] shadow-[0_14px_28px_-12px_rgba(255,77,109,0.55)]"
              style={{ background: POP.coral, color: "white" }}
            >
              Book a 10-Minute Demo
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>

          {/* Trust line */}
          <div className="mt-20 flex items-center gap-8 flex-wrap text-xs text-[#1B1340]/50 font-mono uppercase tracking-[0.18em]">
            <span>Made for</span>
            <span className="text-[#1B1340]/70">Furniture</span>
            <span style={{ color: POP.coral }}>·</span>
            <span className="text-[#1B1340]/70">Home &amp; Decor</span>
            <span style={{ color: POP.amber }}>·</span>
            <span className="text-[#1B1340]/70">Fashion &amp; Boutiques</span>
            <span style={{ color: POP.violet }}>·</span>
            <span className="text-[#1B1340]/70">D2C</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Animated SVG: Brand owner setting up ─────────────────────────────────
function SetupHuman() {
  return (
    <motion.svg
      viewBox="0 0 320 320"
      width="320"
      height="320"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="select-none"
    >
      {/* big colorful halo behind the figure */}
      <circle cx="160" cy="170" r="120" fill={POP.amber} opacity="0.35" />
      <circle cx="160" cy="170" r="90" fill={POP.coral} opacity="0.18" />

      {/* desk surface */}
      <rect x="40" y="230" width="240" height="10" rx="2" fill={NAVY} opacity="0.85" />

      {/* laptop body */}
      <g>
        <rect x="80" y="178" width="160" height="52" rx="4" fill={NAVY} />
        <rect x="88" y="186" width="144" height="36" fill={POP.lime} />
        {/* live screen content — cycling colored bars */}
        <motion.rect
          x="96" y="192" width="50" height="6" rx="1" fill={POP.coral}
          animate={{ width: [50, 78, 50] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.rect
          x="96" y="202" width="80" height="6" rx="1" fill={POP.violet}
          animate={{ width: [80, 110, 80] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
        />
        <motion.rect
          x="96" y="212" width="60" height="6" rx="1" fill={POP.cyan}
          animate={{ width: [60, 92, 60] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
        />
      </g>

      {/* head bobbing + torso */}
      <motion.g
        animate={{ y: [0, -3, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        {/* torso/shirt */}
        <path d="M115 178 Q160 142 205 178 L205 198 L115 198 Z" fill={POP.coral} />
        {/* neck */}
        <rect x="150" y="116" width="20" height="22" fill="#F2C9A7" />
        {/* head */}
        <circle cx="160" cy="100" r="28" fill="#F2C9A7" />
        {/* simple smile */}
        <path d="M150 106 Q160 114 170 106" stroke={NAVY} strokeWidth="2.5" fill="none" strokeLinecap="round" />
        {/* eyes */}
        <circle cx="150" cy="96" r="2.5" fill={NAVY} />
        <circle cx="170" cy="96" r="2.5" fill={NAVY} />
        {/* hair */}
        <path d="M132 96 Q138 64 160 64 Q186 64 188 96 Q186 82 168 76 Q146 76 132 96 Z" fill={NAVY} />
        {/* arms typing — clearly animated up/down */}
      </motion.g>

      {/* LEFT arm — visibly types */}
      <motion.g
        animate={{ rotate: [0, -8, 0] }}
        transition={{ duration: 0.8, repeat: Infinity, ease: "easeInOut" }}
        style={{ transformOrigin: "120px 170px" }}
      >
        <path d="M120 170 L100 200" stroke={POP.coral} strokeWidth="14" strokeLinecap="round" />
        <circle cx="100" cy="200" r="7" fill="#F2C9A7" />
      </motion.g>
      {/* RIGHT arm — types out of phase */}
      <motion.g
        animate={{ rotate: [0, 8, 0] }}
        transition={{ duration: 0.8, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
        style={{ transformOrigin: "200px 170px" }}
      >
        <path d="M200 170 L220 200" stroke={POP.coral} strokeWidth="14" strokeLinecap="round" />
        <circle cx="220" cy="200" r="7" fill="#F2C9A7" />
      </motion.g>

      {/* floating content elements above — bigger animation */}
      <motion.g
        animate={{ y: [0, -14, 0], rotate: [-2, 2, -2] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
        style={{ transformOrigin: "66px 70px" }}
      >
        <rect x="40" y="48" width="52" height="36" rx="4" fill={POP.amber} />
        <rect x="48" y="56" width="28" height="3" fill={NAVY} />
        <rect x="48" y="63" width="36" height="3" fill={NAVY} opacity="0.55" />
        <rect x="48" y="70" width="22" height="3" fill={NAVY} opacity="0.35" />
      </motion.g>
      <motion.g
        animate={{ y: [0, -18, 0], rotate: [3, -3, 3] }}
        transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
        style={{ transformOrigin: "260px 70px" }}
      >
        <rect x="232" y="50" width="52" height="44" rx="4" fill={POP.cyan} />
        <circle cx="258" cy="72" r="11" fill={NAVY} />
        <rect x="248" y="86" width="20" height="3" rx="1" fill={NAVY} />
      </motion.g>
      <motion.g
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 3.0, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
        style={{ transformOrigin: "160px 30px" }}
      >
        <rect x="128" y="24" width="64" height="26" rx="4" fill={POP.violet} />
        <rect x="138" y="32" width="44" height="3" rx="1" fill="white" />
        <rect x="138" y="40" width="28" height="3" rx="1" fill="white" opacity="0.7" />
      </motion.g>

      {/* sparkles — twinkle */}
      <motion.path
        d="M90 130 l3 -7 l3 7 l7 3 l-7 3 l-3 7 l-3 -7 l-7 -3 z"
        fill={POP.lime}
        animate={{ opacity: [0.3, 1, 0.3], scale: [0.85, 1.15, 0.85] }}
        transition={{ duration: 1.6, repeat: Infinity }}
        style={{ transformOrigin: "93px 133px" }}
      />
      <motion.path
        d="M230 138 l2 -5 l2 5 l5 2 l-5 2 l-2 5 l-2 -5 l-5 -2 z"
        fill={POP.amber}
        animate={{ opacity: [0.4, 1, 0.4], scale: [0.85, 1.2, 0.85] }}
        transition={{ duration: 1.8, repeat: Infinity, delay: 0.5 }}
        style={{ transformOrigin: "232px 140px" }}
      />
    </motion.svg>
  );
}

// ─── Animated SVG: Customer interacting with the screen ───────────────────
function CustomerHuman() {
  return (
    <motion.svg
      viewBox="0 0 320 320"
      width="320"
      height="320"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="select-none"
    >
      {/* warm halo */}
      <circle cx="160" cy="170" r="120" fill={POP.coral} opacity="0.32" />
      <circle cx="160" cy="170" r="90" fill={POP.amber} opacity="0.22" />

      {/* device on a pedestal */}
      <rect x="220" y="240" width="60" height="6" rx="2" fill={NAVY} opacity="0.85" />
      <rect x="234" y="200" width="32" height="44" fill={NAVY} opacity="0.85" />

      {/* device screen */}
      <rect x="190" y="80" width="120" height="160" rx="6" fill={NAVY} />
      <rect x="198" y="92" width="104" height="136" fill="white" />
      {/* live screen content — cycling colored cards with strong slide */}
      <motion.g
        animate={{ x: [0, 4, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
      >
        <rect x="208" y="102" width="84" height="18" rx="2" fill={POP.cyan} />
      </motion.g>
      <motion.g
        animate={{ x: [0, -4, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
      >
        <rect x="208" y="126" width="84" height="18" rx="2" fill={POP.amber} />
      </motion.g>
      <motion.g
        animate={{ x: [0, 4, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
      >
        <rect x="208" y="150" width="84" height="18" rx="2" fill={POP.violet} />
      </motion.g>
      {/* big lime match check that pulses */}
      <motion.g
        animate={{ scale: [1, 1.18, 1] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        style={{ transformOrigin: "250px 198px" }}
      >
        <circle cx="250" cy="198" r="20" fill={POP.lime} />
        <path d="M240 198 l6 6 l13 -13" stroke={NAVY} strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </motion.g>

      {/* customer figure — head bobs slightly */}
      <motion.g
        animate={{ y: [0, -3, 0] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      >
        {/* head */}
        <circle cx="100" cy="100" r="28" fill="#D9A982" />
        {/* hair */}
        <path d="M74 100 Q70 70 100 68 Q130 70 126 96 Q116 82 100 82 Q84 84 78 96 Z" fill={NAVY} />
        {/* face */}
        <circle cx="92" cy="96" r="2.5" fill={NAVY} />
        <circle cx="108" cy="96" r="2.5" fill={NAVY} />
        <path d="M92 110 Q100 116 108 110" stroke={NAVY} strokeWidth="2.5" fill="none" strokeLinecap="round" />
        {/* neck */}
        <rect x="90" y="120" width="20" height="18" fill="#D9A982" />
      </motion.g>

      {/* body */}
      <path d="M62 138 Q100 124 138 138 L143 218 L57 218 Z" fill={POP.amber} />
      {/* pants */}
      <rect x="64" y="216" width="32" height="60" rx="3" fill={NAVY} />
      <rect x="104" y="216" width="32" height="60" rx="3" fill={NAVY} />
      {/* shoes */}
      <rect x="58" y="276" width="42" height="7" rx="2" fill={POP.coral} />
      <rect x="100" y="276" width="42" height="7" rx="2" fill={POP.coral} />

      {/* pointing arm tapping the screen — clearly animated forward and back */}
      <motion.g
        animate={{ x: [0, 18, 0] }}
        transition={{ duration: 1.1, repeat: Infinity, ease: [0.6, 0, 0.4, 1] }}
      >
        <path d="M138 156 Q172 156 192 168" stroke={POP.amber} strokeWidth="14" strokeLinecap="round" fill="none" />
        <circle cx="195" cy="168" r="8" fill="#D9A982" />
      </motion.g>

      {/* other arm at side */}
      <path d="M62 156 Q52 192 68 220" stroke={POP.amber} strokeWidth="14" strokeLinecap="round" fill="none" />

      {/* tap ripple emanating from finger */}
      <motion.circle
        cx="200"
        cy="166"
        r="10"
        fill="none"
        stroke={POP.coral}
        strokeWidth="3"
        animate={{ r: [10, 36, 10], opacity: [1, 0, 1] }}
        transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* delight sparkles */}
      <motion.path
        d="M40 60 l3 -7 l3 7 l7 3 l-7 3 l-3 7 l-3 -7 l-7 -3 z"
        fill={POP.lime}
        animate={{ opacity: [0.3, 1, 0.3], scale: [0.85, 1.2, 0.85] }}
        transition={{ duration: 1.6, repeat: Infinity }}
        style={{ transformOrigin: "43px 63px" }}
      />
      <motion.path
        d="M285 50 l3 -7 l3 7 l7 3 l-7 3 l-3 7 l-3 -7 l-7 -3 z"
        fill={POP.cyan}
        animate={{ opacity: [0.4, 1, 0.4], scale: [0.85, 1.2, 0.85] }}
        transition={{ duration: 1.8, repeat: Infinity, delay: 0.4 }}
        style={{ transformOrigin: "288px 53px" }}
      />
    </motion.svg>
  );
}

// ─── Infographic 1: 4 Steps to Launch (for the brand owner) ──────────────
function FourStepsToLaunch() {
  const steps = [
    { n: 1, Icon: Building2,          color: POP.coral,    title: "Add brand info",  blurb: "Logo, accent color, currency. 2 minutes." },
    { n: 2, Icon: Boxes,              color: POP.amber,    title: "Upload products", blurb: "Drag a CSV. Images auto-download." },
    { n: 3, Icon: MessageSquareQuote, color: POP.violet,   title: "Write questions", blurb: "3–7 questions. AI suggests them too." },
    { n: 4, Icon: Tv2,                color: POP.cyan,     title: "Launch anywhere", blurb: "One link, one QR. Works on any device — tablet, phone, web." },
  ];
  return (
    <section className="px-8 py-20 lg:py-28" style={{ background: BG_BLUSH_SOFT }}>
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-[1fr_320px] gap-10 lg:gap-16 items-start">
          {/* LEFT: copy */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: POP.coral }}>
                For you · the brand owner
              </span>
            </div>
            <h2 className="font-display font-medium tracking-[-0.03em] text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl" style={{ color: NAVY }}>
              Set it up in <span style={{ color: POP.amber }}>four steps</span>.
            </h2>
            <p className="mt-5 text-base lg:text-lg max-w-2xl text-[#1B1340]/60">
              No code. No installs. No training videos. The whole setup fits on this page.
            </p>
          </div>

          {/* RIGHT: animated human illustration */}
          <div className="hidden lg:flex justify-end -mt-4">
            <SetupHuman />
          </div>
        </div>

        {/* Step row */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {steps.map(({ n, Icon, color, title, blurb }, i) => (
            <motion.div
              key={n}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.45, delay: i * 0.08, ease: [0.2, 0.6, 0.2, 1] }}
              className="relative bg-white border p-6 shadow-[0_8px_24px_-12px_rgba(255,77,109,0.18)]"
              style={{ borderColor: "rgba(27,19,64,0.08)" }}
            >
              {/* big colored circle with icon */}
              <div className="flex items-center gap-4 mb-5">
                <div
                  className="relative w-14 h-14 grid place-items-center"
                  style={{ background: color }}
                >
                  <Icon className="w-6 h-6" color={INK} strokeWidth={1.6} />
                  <span
                    className="absolute -top-2 -right-2 w-6 h-6 grid place-items-center text-[11px] font-mono font-medium"
                    style={{ background: INK, color: color, border: `1.5px solid ${color}` }}
                  >
                    {n}
                  </span>
                </div>
                <div className="font-display font-medium text-lg" style={{ color: NAVY }}>{title}</div>
              </div>
              <p className="text-sm text-[#1B1340]/65 leading-relaxed">{blurb}</p>
              {/* connector dot row (visual only — hidden on last + on mobile) */}
              {i < steps.length - 1 && (
                <span className="hidden lg:block absolute top-[34px] -right-[14px] w-2 h-2 rounded-full" style={{ background: color, opacity: 0.45 }} />
              )}
            </motion.div>
          ))}
        </div>

        {/* footer line */}
        <div className="mt-12 flex items-center gap-3 text-sm">
          <span className="text-[#1B1340]/55">Don&apos;t write a single line of code.</span>
        </div>
      </div>
    </section>
  );
}

// ─── Infographic 2: Customer Kiosk Flow ─────────────────────────────────
function CustomerKioskFlow() {
  return (
    <section className="px-8 py-20 lg:py-28" style={{ background: BG_VIOLET_SOFT, color: SLATE }}>
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-[1fr_320px] gap-10 lg:gap-16 items-start">
          {/* LEFT: copy */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: POP.coral }}>
                For them · the customer
              </span>
            </div>
            <h2 className="font-display font-medium tracking-[-0.03em] text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
              Three taps. <span style={{ color: POP.coral }}>One perfect match.</span>
            </h2>
            <p className="mt-5 text-base lg:text-lg max-w-2xl text-[#5E6660]">
              Your customer walks up to the screen. Sixty seconds later they&apos;re holding the right product.
              That&apos;s the whole experience.
            </p>
          </div>

          {/* RIGHT: animated human illustration */}
          <div className="hidden lg:flex justify-end -mt-4">
            <CustomerHuman />
          </div>
        </div>

        {/* Three illustrated device mockups in a row — works on any screen */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          <FlowCard
            n={1}
            color={POP.coral}
            label="They tap"
            sub="The kiosk wakes up."
            Icon={Hand}
            mock={
              <div className="absolute inset-0 grid place-items-center bg-[#0F1210] text-white">
                <div className="text-center">
                  <div className="font-display text-2xl tracking-tight">Belle Vue</div>
                  <div className="mt-3 text-xs text-[#1B1340]/55 font-mono uppercase tracking-[0.22em]">Tap anywhere to begin</div>
                  <div className="mt-6 w-8 h-8 mx-auto rounded-full border border-white/40 grid place-items-center animate-pulse">
                    <Hand className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
            }
          />
          <FlowCard
            n={2}
            color={POP.lavender}
            label="They answer 3-7 questions"
            sub="Quick taps. No typing. No friction."
            Icon={MessageSquareQuote}
            mock={
              <div className="absolute inset-0 p-5 bg-white">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#5E6660]">Question 2 of 5</div>
                <div className="mt-2 font-display text-[15px] leading-snug text-[#0F1210]">What&apos;s the occasion?</div>
                <div className="mt-3 space-y-1.5">
                  {[
                    { t: "Everyday", c: POP.amber },
                    { t: "Special evening", c: POP.coral, on: true },
                    { t: "Workout", c: POP.mint },
                  ].map(({ t, c, on }) => (
                    <div
                      key={t}
                      className="text-[11px] py-2 px-2.5 border flex items-center gap-2"
                      style={{
                        borderColor: on ? c : "rgba(15,18,16,0.12)",
                        background: on ? `${c}22` : "transparent",
                        color: "#0F1210",
                      }}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ background: c }} />
                      {t}
                    </div>
                  ))}
                </div>
              </div>
            }
          />
          <FlowCard
            n={3}
            color={POP.mint}
            label="They see their match"
            sub="AI picks the one product. Plus 2 alts."
            Icon={Wand2}
            mock={
              <div className="absolute inset-0 bg-[#0F1210] text-white p-5 flex flex-col">
                <div className="font-mono text-[9px] uppercase tracking-[0.22em]" style={{ color: POP.mint }}>
                  Your best match
                </div>
                <div className="mt-2 font-display text-[15px] tracking-tight">Aavish Slip Dress · Beige</div>
                <div
                  className="mt-3 flex-1 grid place-items-center"
                  style={{ background: `linear-gradient(135deg, ${POP.mint}40, ${POP.lavender}40)` }}
                >
                  <Sparkles className="w-8 h-8" color={POP.mint} />
                </div>
                <div className="mt-3 flex items-center justify-between text-[10px]">
                  <span className="text-[#1B1340]/65">Match score</span>
                  <span style={{ color: POP.mint }}>96%</span>
                </div>
              </div>
            }
          />
        </div>

        {/* Conversion line */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { stat: "60s", label: "Average kiosk session", color: POP.coral },
            { stat: "3×",  label: "Higher AOV vs. self-browse", color: POP.amber },
            { stat: "0",   label: "Apps to install — runs in any browser", color: POP.cyan },
          ].map((s, i) => (
            <div
              key={i}
              className="p-5 border bg-white/60"
              style={{ borderColor: "rgba(15,18,16,0.1)" }}
            >
              <div className="font-display font-medium text-4xl tracking-tight" style={{ color: s.color }}>
                {s.stat}
              </div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#5E6660]">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FlowCard({ n, color, label, sub, Icon, mock }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.5, delay: (n - 1) * 0.1, ease: [0.2, 0.6, 0.2, 1] }}
      className="relative"
    >
      {/* device-agnostic frame — iPad, phone, or browser */}
      <div className="relative aspect-[3/4] border-[6px] border-[#0F1210] bg-white shadow-[0_18px_40px_-20px_rgba(15,18,16,0.25)] overflow-hidden">
        {mock}
      </div>
      {/* label below the device */}
      <div className="mt-4 flex items-start gap-3">
        <div
          className="w-9 h-9 grid place-items-center shrink-0"
          style={{ background: color }}
        >
          <span className="font-mono text-sm font-medium" style={{ color: INK }}>{n}</span>
        </div>
        <div>
          <div className="font-display font-medium text-[15px] leading-tight text-[#0F1210]">{label}</div>
          <div className="text-sm text-[#5E6660] mt-1">{sub}</div>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Problem Block ────────────────────────────────────────────────────────
function ProblemBlock() {
  return (
    <section className="px-8 py-20 lg:py-28" style={{ background: BG_CYAN_SOFT }}>
      <div className="max-w-7xl mx-auto">
        <div
          className="relative rounded-[28px] p-10 lg:p-16"
          style={{ background: "#FFFFFFCC", color: SLATE, backdropFilter: "blur(8px)" }}
        >
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-6 h-px" style={{ background: SAGE_DARK }} />
                <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: SAGE_DARK }}>
                  The Why
                </span>
              </div>
              <h2 className="font-display font-medium tracking-[-0.03em] text-3xl lg:text-4xl leading-[1.05]" style={{ color: SLATE }}>
                Static forms<br />
                drop revenue.
              </h2>
            </div>
            <div className="lg:col-span-8 lg:pt-6">
              <p className="text-xl lg:text-2xl font-display font-medium leading-[1.35] tracking-[-0.01em]" style={{ color: SLATE }}>
                Static sign-up sheets and generic web forms drop revenue. Modern experiential brands need to
                guide consumer choice. Getcurate captures rich,{" "}
                <span style={{ color: SAGE_DARK, fontStyle: "italic", fontWeight: 500 }}>
                  zero-party preference data
                </span>{" "}
                at every touchpoint and pushes it directly to Shopify and Klaviyo—even if the venue&apos;s Wi-Fi
                drops completely.
              </p>
              <div className="mt-10 grid sm:grid-cols-3 gap-4">
                <Pill Icon={Database} label="Zero-party data" />
                <Pill Icon={WifiOff} label="Offline-resilient" />
                <Pill Icon={ShieldCheck} label="Direct to your CRM" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pill({ Icon, label }) {
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 rounded-full border"
      style={{ borderColor: "rgba(15,18,16,0.12)", color: SLATE }}
    >
      <Icon className="w-4 h-4" style={{ color: SAGE_DARK }} />
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}

// ─── Verticals (Tabbed) ───────────────────────────────────────────────────
function VerticalsSection() {
  const [active, setActive] = useState(VERTICALS[0].id);
  const current = VERTICALS.find((v) => v.id === active);

  return (
    <section id="verticals" className="px-8 py-24 lg:py-32">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-6 h-px" style={{ background: SAGE }} />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: SAGE }}>
                Use Cases
              </span>
            </div>
            <h2 className="font-display font-medium tracking-[-0.03em] text-4xl lg:text-5xl leading-[1.05]">
              One engine.<br />
              <span className="text-[#1B1340]/60">Four distinct retail moments.</span>
            </h2>
          </div>
          <p className="text-[#1B1340]/60 lg:max-w-sm text-sm leading-relaxed">
            Tap a vertical to see the exact pain point, our solution, and the metric our partners report.
          </p>
        </div>

        {/* Tab strip */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          {VERTICALS.map((v) => {
            const isActive = v.id === active;
            const Icon = v.Icon;
            return (
              <button
                key={v.id}
                onClick={() => setActive(v.id)}
                data-testid={`vertical-tab-${v.id}`}
                className="group relative text-left rounded-2xl border p-5 transition-all"
                style={{
                  borderColor: isActive ? SAGE : "rgba(27,19,64,0.12)",
                  background: isActive ? "rgba(255,77,109,0.06)" : "white",
                }}
              >
                <div className="flex items-start justify-between">
                  <Icon
                    className="w-5 h-5 transition-colors"
                    style={{ color: isActive ? SAGE : "rgba(27,19,64,0.45)" }}
                  />
                  <span className="font-mono text-[10px] tracking-[0.2em] text-[#1B1340]/40">{v.eyebrow}</span>
                </div>
                <div
                  className="mt-8 text-sm font-medium leading-snug"
                  style={{ color: isActive ? NAVY : "rgba(27,19,64,0.6)" }}
                >
                  {v.title}
                </div>
              </button>
            );
          })}
        </div>

        {/* Panel */}
        <div className="mt-6 rounded-[28px] overflow-hidden border" style={{ borderColor: "rgba(27,19,64,0.08)", background: "white", color: SLATE }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="grid lg:grid-cols-12 gap-10 p-10 lg:p-14"
              data-testid={`vertical-panel-${current.id}`}
            >
              <div className="lg:col-span-7 space-y-10">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: SAGE_DARK }}>
                    {current.painLabel}
                  </div>
                  <p className="mt-4 text-xl lg:text-2xl font-display font-medium leading-[1.35] tracking-[-0.01em]" style={{ color: SLATE }}>
                    {current.painText}
                  </p>
                </div>
                <div
                  className="pl-8 border-l-2"
                  style={{ borderColor: SAGE }}
                >
                  <div className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: SAGE_DARK }}>
                    {current.solutionLabel}
                  </div>
                  <p className="mt-4 text-lg leading-[1.55]" style={{ color: SLATE }}>
                    {current.solutionText}
                  </p>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div
                  className="rounded-[20px] p-8 lg:p-10 h-full flex flex-col justify-between min-h-[280px]"
                  style={{ background: INK, color: "#fff" }}
                >
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#1B1340]/50">
                      Core Metric
                    </div>
                    <div
                      className="mt-6 font-display font-medium tracking-[-0.05em] text-[96px] leading-none"
                      style={{ color: SAGE }}
                    >
                      {current.metric}
                    </div>
                  </div>
                  <div className="mt-8">
                    <div className="text-sm text-[#1B1340]/80 leading-relaxed max-w-[15rem]">
                      {current.metricSub}
                    </div>
                    <div className="mt-6 flex items-center gap-3 text-[#1B1340]/50">
                      <span className="w-6 h-px bg-white/30" />
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em]">
                        Reported by partners
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

// ─── Deployment Journey ──────────────────────────────────────────────────
const JOURNEY = [
  {
    n: "1",
    Icon: UploadCloud,
    eyebrow: "Synced & Structured Setup",
    title: "Map in Minutes",
    body:
      "Log in, easily upload your product catalog via a clean CSV or fetch inventory tags directly from your store. Use our intuitive logic builder to map your specific consumer questions to your product variants.",
    metaLeft: "CSV · Shopify sync",
    metaRight: "≈ 8 min",
  },
  {
    n: "2",
    Icon: QrCode,
    eyebrow: "Instant URL & QR Code Freedom",
    title: "One Link, Total Freedom",
    body:
      "Getcurate instantly renders a high-speed, unique Web URL and a dynamic, high-resolution QR code file for your brand. There is no software to install, no clunky native app updates, and zero code required.",
    metaLeft: "Web URL · QR · PWA",
    metaRight: "0 install",
  },
  {
    n: "3",
    Icon: MonitorSmartphone,
    eyebrow: "Omnichannel Activation",
    title: "Deploy Everywhere",
    body:
      "Embed the URL natively on your storefront via a sleek iframe, link to it directly within email newsletter blocks, or print the custom QR code on premium countertop displays and product tags for the retail floor.",
    metaLeft: "Site · Email · Tablet · Print",
    metaRight: "Any surface",
  },
];

function DeploymentJourney() {
  return (
    <section id="how-it-works" className="px-8 py-24 lg:py-32">
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-6 h-px" style={{ background: SAGE }} />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: SAGE }}>
                The Zero-IT Deployment Journey
              </span>
            </div>
            <h2 className="font-display font-medium tracking-[-0.03em] text-4xl lg:text-5xl leading-[1.05]">
              From catalog to countertop in{" "}
              <span style={{ color: SAGE, fontStyle: "italic", fontWeight: 400 }}>three steps.</span>
            </h2>
          </div>
          <p className="text-[#1B1340]/60 lg:max-w-sm text-sm leading-relaxed">
            No engineering tickets. No native apps. No vendor lock-in. The full journey from brand login to
            live customer experience takes one afternoon.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* connecting line — desktop only */}
          <div
            className="hidden lg:block absolute top-[88px] left-0 right-0 h-px pointer-events-none"
            style={{ background: "linear-gradient(to right, transparent, rgba(139,157,131,0.45), transparent)" }}
          />

          <div className="grid lg:grid-cols-3 gap-5 lg:gap-7">
            {JOURNEY.map((step, idx) => {
              const Icon = step.Icon;
              return (
                <motion.div
                  key={step.n}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.55, delay: idx * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  className="relative"
                  data-testid={`journey-step-${step.n}`}
                >
                  {/* Step number badge */}
                  <div className="relative flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                      <div
                        className="relative w-[60px] h-[60px] rounded-2xl flex items-center justify-center font-display font-medium text-2xl"
                        style={{
                          background: INK,
                          color: SAGE,
                          border: `1px solid ${SAGE}`,
                          boxShadow: `0 0 0 6px rgba(139,157,131,0.08)`,
                        }}
                      >
                        {step.n}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#1B1340]/45">
                        Step {step.n} / 03
                      </div>
                    </div>
                    {idx < JOURNEY.length - 1 && (
                      <ArrowRight className="hidden lg:block w-5 h-5 text-white/25 absolute -right-7 top-1/2 -translate-y-1/2" />
                    )}
                  </div>

                  {/* Card */}
                  <div
                    className="rounded-[24px] border p-8 lg:p-9 h-full transition-all hover:-translate-y-0.5 group"
                    style={{
                      borderColor: "rgba(255,255,255,0.1)",
                      background: "rgba(255,255,255,0.02)",
                      minHeight: 360,
                    }}
                  >
                    <div className="flex items-start justify-between mb-7">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ background: "rgba(139,157,131,0.12)" }}
                      >
                        <Icon className="w-5 h-5" style={{ color: SAGE }} />
                      </div>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1B1340]/45 border border-white/10 rounded-full px-2.5 py-1">
                        {step.eyebrow}
                      </span>
                    </div>

                    <h3 className="font-display font-medium tracking-[-0.015em] text-3xl leading-[1.1]">
                      {step.title}
                    </h3>
                    <p className="mt-4 text-sm text-[#1B1340]/65 leading-relaxed">{step.body}</p>

                    <div
                      className="mt-8 pt-5 flex items-center justify-between text-[11px] font-mono uppercase tracking-[0.18em]"
                      style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}
                    >
                      <span className="text-[#1B1340]/55">{step.metaLeft}</span>
                      <span style={{ color: SAGE }}>{step.metaRight}</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Integrations ─────────────────────────────────────────────────────────
function IntegrationsSection() {
  return (
    <section id="integrations" className="px-8 py-24 lg:py-32" style={{ background: "rgba(255,255,255,0.015)" }}>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-6 h-px" style={{ background: SAGE }} />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: SAGE }}>
                Integrations
              </span>
            </div>
            <h2 className="font-display font-medium tracking-[-0.03em] text-4xl lg:text-5xl leading-[1.05]">
              Plugs into your<br />
              <span className="text-[#1B1340]/60">existing revenue stack.</span>
            </h2>
          </div>
          <p className="text-[#1B1340]/60 lg:max-w-sm text-sm leading-relaxed">
            Native data pathways. No middleware. Quiz answers become preference attributes the moment a
            customer hits &ldquo;submit.&rdquo;
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {INTEGRATIONS.map(({ name, Icon, blurb, tag }) => (
            <div
              key={name}
              data-testid={`integration-${name.split(" ")[0].toLowerCase()}`}
              className="group relative rounded-2xl border p-8 transition-all hover:-translate-y-0.5"
              style={{ borderColor: "rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.02)" }}
            >
              <div className="flex items-start justify-between mb-12">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center"
                  style={{ background: "rgba(139,157,131,0.12)" }}
                >
                  <Icon className="w-5 h-5" style={{ color: SAGE }} />
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1B1340]/40 border border-white/10 rounded-full px-2.5 py-1">
                  {tag}
                </span>
              </div>
              <h3 className="font-display font-medium text-2xl tracking-[-0.01em]">{name}</h3>
              <p className="mt-3 text-sm text-[#1B1340]/65 leading-relaxed">{blurb}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Product Showcase (real product screenshots) ──────────────────────────
function ProductShowcase() {
  return (
    <section className="px-8 py-20 lg:py-28" style={{ background: BG_CREAM }}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-3">
          <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: POP.coral }}>
            See it in action
          </span>
        </div>
        <h2 className="font-display font-medium tracking-[-0.03em] text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-4xl" style={{ color: NAVY }}>
          What your shoppers see.<br />
          What <span style={{ color: POP.coral, fontStyle: "italic", fontWeight: 500 }}>you</span> see.
        </h2>
        <p className="mt-5 text-base lg:text-lg max-w-2xl text-[#1B1340]/60">
          The kiosk your customer taps through, and the dashboard where you track everything.
        </p>

        <div className="mt-14 grid lg:grid-cols-2 gap-8 lg:gap-10">
          {/* Kiosk reveal shot */}
          <motion.figure
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="group"
          >
            <div className="relative overflow-hidden bg-white shadow-[0_28px_50px_-24px_rgba(27,19,64,0.28)] ring-1 ring-black/5">
              <img
                src="/screens/kiosk-reveal.png"
                alt="Getcurate kiosk showing an AI-recommended product with the closest store to the customer"
                className="block w-full h-auto"
                loading="lazy"
              />
            </div>
            <figcaption className="mt-5 flex items-baseline gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#1B1340]/55">01 · Kiosk reveal</span>
              <span className="text-sm text-[#1B1340]/75">
                The exact product, price, and closest store carrying it.
              </span>
            </figcaption>
          </motion.figure>

          {/* Analytics dashboard shot */}
          <motion.figure
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="group"
          >
            <div className="relative overflow-hidden bg-white shadow-[0_28px_50px_-24px_rgba(27,19,64,0.28)] ring-1 ring-black/5">
              <img
                src="/screens/dashboard.png"
                alt="Getcurate brand dashboard showing product count, questions, experiences and session analytics"
                className="block w-full h-auto"
                loading="lazy"
              />
            </div>
            <figcaption className="mt-5 flex items-baseline gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#1B1340]/55">02 · Your dashboard</span>
              <span className="text-sm text-[#1B1340]/75">
                Live visits, most-recommended products, session history — one view.
              </span>
            </figcaption>
          </motion.figure>
        </div>
      </div>
    </section>
  );
}

// ─── Enterprise Analytics ─────────────────────────────────────────────────
const KPI_CARDS = [
  {
    id: "revenue",
    bucket: "The Cash Register",
    badge: "Commercial KPIs",
    Icon: TrendingUp,
    title: "Quiz-Attributed Revenue & AOV Uplift",
    body:
      "Track the exact Euro amount generated from customers who purchase a recommended product. See the real-time lift in Average Order Value (AOV) as Getcurate intelligently bundles matching items based on buyer responses.",
    headline: "€12,480",
    headlineSub: "Quiz-attributed revenue · 30d",
    series: [22, 28, 26, 35, 33, 41, 48, 52, 50, 58, 64, 71],
    metricRow: [
      { k: "AOV uplift", v: "+18%" },
      { k: "Quiz → cart", v: "32%" },
    ],
  },
  {
    id: "leads",
    bucket: "The Audience Builder",
    badge: "Marketing KPIs",
    Icon: Users,
    title: "Zero-Party Lead Capture Rate",
    body:
      "Monitor your exact customer acquisition funnel. See how many offline foot-traffic visitors or email subscribers completed the experience and handed over authenticated Email and SMS profiles to unlock their results.",
    headline: "1,284",
    headlineSub: "Verified email & SMS profiles · 30d",
    funnel: [
      { k: "Quiz started", v: 100 },
      { k: "Reached results", v: 84 },
      { k: "Submitted email", v: 71 },
      { k: "Opted in SMS", v: 49 },
    ],
  },
  {
    id: "stock",
    bucket: "The Stock Predictor",
    badge: "Inventory KPIs",
    Icon: Package,
    title: "Demand-to-Stock Forecasting",
    body:
      "A hidden goldmine for retail merchants. View aggregate shopper preference distributions (e.g., '45% of visitors have Dry Skin' or '60% wear Size Medium') so you can dynamically reroute store inventory before items sell out.",
    headline: "60%",
    headlineSub: "Visitors looking for Size M",
    bars: [
      { k: "Dry Skin", v: 45 },
      { k: "Normal", v: 32 },
      { k: "Oily", v: 23 },
      { k: "Size M", v: 60 },
      { k: "Size L", v: 28 },
      { k: "Size S", v: 12 },
    ],
  },
  {
    id: "funnel",
    bucket: "The Funnel Optimizer",
    badge: "Conversion KPIs",
    Icon: Activity,
    title: "Friction & Drop-Off Leakage Tracking",
    body:
      "Getcurate maps exactly where users exit your quiz questionnaire. If a specific question causes friction, identify it instantly so you can refine your logic and maximize completion rates without writing code.",
    headline: "Q3",
    headlineSub: "Highest drop-off · refine this",
    drop: [
      { k: "Q1 · Vibe", retained: 100 },
      { k: "Q2 · Color", retained: 92 },
      { k: "Q3 · Size", retained: 71 },
      { k: "Q4 · Budget", retained: 64 },
      { k: "Results", retained: 58 },
    ],
  },
];

function AnalyticsSection() {
  return (
    <section id="analytics" className="px-8 py-24 lg:py-32" style={{ background: "rgba(255,255,255,0.015)" }}>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-6 h-px" style={{ background: SAGE }} />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: SAGE }}>
                Real-time consumer intelligence
              </span>
            </div>
            <h2 className="font-display font-medium tracking-[-0.03em] text-4xl lg:text-5xl leading-[1.05]">
              Priceless Consumer Intelligence,<br />
              <span style={{ color: SAGE, fontStyle: "italic", fontWeight: 400 }}>Visualized.</span>
            </h2>
          </div>
          <p className="text-[#1B1340]/60 lg:max-w-sm text-sm leading-relaxed">
            Stop guessing what your shoppers want. Look at the data engine that justifies your subscription on day one.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          {KPI_CARDS.map((c) => (
            <KpiCard key={c.id} c={c} />
          ))}
        </div>
      </div>
    </section>
  );
}

function KpiCard({ c }) {
  const Icon = c.Icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-[24px] border p-7 lg:p-8 group relative overflow-hidden"
      style={{
        borderColor: "rgba(255,255,255,0.1)",
        background: "linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0.005) 100%)",
      }}
      data-testid={`kpi-${c.id}`}
    >
      <div
        className="absolute -top-32 -right-32 w-72 h-72 rounded-full opacity-[0.05] group-hover:opacity-[0.08] transition-opacity"
        style={{ background: SAGE }}
      />
      <div className="relative">
        <div className="flex items-start justify-between mb-7">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(139,157,131,0.12)" }}
            >
              <Icon className="w-4 h-4" style={{ color: SAGE }} />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#1B1340]/45">{c.bucket}</div>
              <div className="text-xs text-[#1B1340]/65 mt-0.5">{c.badge}</div>
            </div>
          </div>
        </div>

        <h3 className="font-display font-medium text-2xl tracking-[-0.01em] leading-snug">{c.title}</h3>
        <p className="mt-3 text-sm text-[#1B1340]/60 leading-relaxed">{c.body}</p>

        {/* Mock chart area */}
        <div
          className="mt-6 rounded-2xl p-6"
          style={{ background: INK, border: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#1B1340]/45">
                {c.headlineSub}
              </div>
              <div
                className="mt-2 font-display font-medium tracking-[-0.04em] text-5xl"
                style={{ color: SAGE }}
              >
                {c.headline}
              </div>
            </div>
            <Icon className="w-5 h-5 text-white/30" />
          </div>

          {/* mini visualization */}
          <div className="mt-6">
            {c.series && <Sparkline values={c.series} />}
            {c.funnel && <FunnelViz steps={c.funnel} />}
            {c.bars && <BarsViz rows={c.bars} />}
            {c.drop && <DropViz steps={c.drop} />}
          </div>

          {c.metricRow && (
            <div className="mt-5 grid grid-cols-2 gap-px" style={{ background: "rgba(255,255,255,0.08)" }}>
              {c.metricRow.map((m) => (
                <div key={m.k} className="bg-[#0F1210] py-3 px-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#1B1340]/50">{m.k}</div>
                  <div className="mt-1 font-display font-medium text-xl" style={{ color: SAGE }}>{m.v}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function Sparkline({ values }) {
  const max = Math.max(...values);
  const min = Math.min(...values);
  const w = 100, h = 28;
  const path = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - ((v - min) / (max - min || 1)) * h;
      return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16" preserveAspectRatio="none">
      <path d={path} stroke={SAGE} strokeWidth="1.2" fill="none" />
      <path d={`${path} L${w},${h} L0,${h} Z`} fill={SAGE} opacity="0.18" />
    </svg>
  );
}

function FunnelViz({ steps }) {
  return (
    <div className="space-y-2">
      {steps.map((s, i) => (
        <div key={s.k} className="flex items-center gap-3">
          <span className="text-xs text-[#1B1340]/60 w-32 shrink-0">{s.k}</span>
          <div className="flex-1 h-2 bg-white/5 relative">
            <div
              className="h-full"
              style={{ width: `${s.v}%`, background: i === 0 ? SAGE : `rgba(139,157,131,${0.85 - i * 0.15})` }}
            />
          </div>
          <span className="font-mono text-xs text-[#1B1340]/75 w-10 text-right">{s.v}%</span>
        </div>
      ))}
    </div>
  );
}

function BarsViz({ rows }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      {rows.slice(0, 6).map((r) => (
        <div key={r.k}>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1B1340]/50">{r.k}</div>
          <div className="mt-1.5 h-1.5 bg-white/5">
            <div className="h-full" style={{ width: `${r.v}%`, background: SAGE }} />
          </div>
          <div className="mt-1 font-mono text-xs text-[#1B1340]/75">{r.v}%</div>
        </div>
      ))}
    </div>
  );
}

function DropViz({ steps }) {
  return (
    <div className="flex items-end gap-2 h-24">
      {steps.map((s, i) => {
        const drop = i > 0 ? steps[i - 1].retained - s.retained : 0;
        const isWorst = drop === Math.max(...steps.map((x, j) => (j > 0 ? steps[j - 1].retained - x.retained : 0)));
        return (
          <div key={s.k} className="flex-1 flex flex-col items-center justify-end">
            <div
              className="w-full transition-colors"
              style={{
                height: `${(s.retained / 100) * 100}%`,
                background: isWorst && drop > 0 ? "#E07A5F" : SAGE,
                opacity: isWorst && drop > 0 ? 0.85 : 0.5 + (s.retained / 200),
              }}
            />
            <div className="mt-2 font-mono text-[9px] text-[#1B1340]/50 truncate w-full text-center">{s.k}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Pricing Section ──────────────────────────────────────────────────────
const PRICING_BULLETS = [
  "Unlimited Quiz Completions & Leads Captured",
  "Instant high-resolution QR code generation for your retail floor",
  "Flawless standalone Web URL (getcurate.shop/yourbrand)",
  "Automated real-time background sync to Shopify & Klaviyo",
  "Full access to the Real-Time Consumer Intelligence Dashboard",
];

function PricingSection({ onBookDemo }) {
  return (
    <section id="pricing" className="px-8 py-24 lg:py-32">
      <div className="max-w-5xl mx-auto text-center mb-14">
        <div className="flex items-center justify-center gap-3 mb-5">
          <span className="w-6 h-px" style={{ background: SAGE }} />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: SAGE }}>
            No-friction pricing
          </span>
          <span className="w-6 h-px" style={{ background: SAGE }} />
        </div>
        <h2 className="font-display font-medium tracking-[-0.035em] text-4xl lg:text-6xl leading-[1.02]">
          Play in the Sandbox.<br />
          <span style={{ color: SAGE, fontStyle: "italic", fontWeight: 400 }}>Pay Only When Ready.</span>
        </h2>
        <p className="mt-6 text-[#1B1340]/60 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto">
          No credit cards. No time-limited trials. Build your entire custom product experience and preview
          it live. Upgrade only when you are ready to publish your URL and generate your unique QR code.
        </p>
      </div>

      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-[32px] overflow-hidden"
          style={{ background: PANEL, color: SLATE }}
          data-testid="pricing-card"
        >
          {/* Decorative top stripe */}
          <div className="h-1.5 w-full" style={{ background: `linear-gradient(to right, ${SAGE}, ${SAGE_DARK})` }} />

          <div className="relative p-10 lg:p-14">
            <div
              className="absolute -top-24 -right-24 w-80 h-80 rounded-full opacity-[0.12] pointer-events-none"
              style={{ background: SAGE }}
            />

            <div className="relative flex items-center gap-3 mb-6">
              <Sparkles className="w-4 h-4" style={{ color: SAGE_DARK }} />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: SAGE_DARK }}>
                Two ways to go live
              </span>
            </div>

            {/* Plan cards */}
            <div className="relative grid sm:grid-cols-2 gap-4 mb-10">
              {/* Monthly */}
              <div
                className="relative p-6 border bg-white"
                style={{ borderColor: "rgba(15,18,16,0.12)" }}
                data-testid="pricing-card-monthly"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "#5E6660" }}>
                  Monthly
                </div>
                <div
                  className="font-display font-medium tracking-[-0.04em] text-5xl lg:text-6xl leading-none mt-3"
                  style={{ color: SLATE }}
                >
                  €10
                  <span className="font-mono text-sm ml-2 text-[#5E6660] tracking-normal">/ month</span>
                </div>
                <p className="mt-3 text-sm" style={{ color: SLATE }}>
                  Try the platform without committing. Cancel any time.
                </p>
              </div>

              {/* Annual — highlighted */}
              <div
                className="relative p-6 border-2 bg-white"
                style={{ borderColor: SAGE_DARK }}
                data-testid="pricing-card-annual"
              >
                <span
                  className="absolute -top-3 left-4 px-2 py-0.5 text-[10px] tracking-[0.22em] uppercase font-mono"
                  style={{ background: SAGE_DARK, color: "white" }}
                >
                  Best value · Save ~17%
                </span>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: SAGE_DARK }}>
                  Annual
                </div>
                <div
                  className="font-display font-medium tracking-[-0.04em] text-5xl lg:text-6xl leading-none mt-3"
                  style={{ color: SLATE }}
                >
                  €100
                  <span className="font-mono text-sm ml-2 text-[#5E6660] tracking-normal">/ year</span>
                </div>
                <p className="mt-3 text-sm" style={{ color: SLATE }}>
                  Works out to <span style={{ color: SAGE_DARK, fontWeight: 500 }}>€8.33 / mo</span>.{" "}
                  Two months free.
                </p>
              </div>
            </div>

            <div className="relative mb-10">
              <p className="text-lg" style={{ color: SLATE }}>
                Unlimited access on both plans.{" "}
                <span style={{ color: SAGE_DARK, fontStyle: "italic", fontWeight: 500 }}>
                  Zero hidden volume limits.
                </span>
              </p>
            </div>

            <div
              className="relative grid sm:grid-cols-2 gap-x-8 gap-y-4 mb-10 pt-8"
              style={{ borderTop: "1px solid rgba(15,18,16,0.1)" }}
            >
              {PRICING_BULLETS.map((b) => (
                <div key={b} className="flex items-start gap-3" data-testid={`pricing-bullet`}>
                  <span
                    className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: SAGE }}
                  >
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </span>
                  <span className="text-sm leading-snug" style={{ color: SLATE }}>
                    {b}
                  </span>
                </div>
              ))}
            </div>

            <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
              <button
                onClick={onBookDemo}
                data-testid="pricing-cta-book-demo"
                className="group flex-1 inline-flex items-center justify-center gap-3 h-14 px-7 rounded-full font-medium transition-all hover:scale-[1.01] shadow-[0_14px_28px_-12px_rgba(255,77,109,0.55)]"
                style={{ background: POP.coral, color: "#fff" }}
              >
                Book a 10-Min Demo
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <Link to="/register" className="sm:w-auto" data-testid="pricing-cta-build-free">
                <button
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full font-medium border transition-colors"
                  style={{ borderColor: "rgba(15,18,16,0.16)", color: SLATE }}
                >
                  Build your first quiz free
                </button>
              </Link>
            </div>
            <p className="relative mt-6 text-xs" style={{ color: "#5E6660" }}>
              No card on file. You only enter payment details once you click <em>Publish</em> on your first experience.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Final CTA ─────────────────────────────────────────────────────────────
function FinalCTA({ onBookDemo }) {
  return (
    <section id="get-started" className="px-8 py-24 lg:py-32">
      <div className="max-w-7xl mx-auto">
        <div
          className="relative rounded-[32px] p-12 lg:p-20 overflow-hidden"
          style={{ background: PANEL, color: SLATE }}
        >
          <div
            className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-[0.12]"
            style={{ background: SAGE }}
          />
          <div className="relative grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-6 h-px" style={{ background: SAGE_DARK }} />
                <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: SAGE_DARK }}>
                  Last conversion point
                </span>
              </div>
              <h2 className="font-display font-medium tracking-[-0.035em] text-5xl lg:text-7xl leading-[0.98]" style={{ color: SLATE }}>
                Stop guessing what<br />
                your customers want.<br />
                <span style={{ color: SAGE_DARK, fontStyle: "italic", fontWeight: 400 }}>
                  Let them tell you.
                </span>
              </h2>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-4">
              <button
                onClick={onBookDemo}
                data-testid="final-cta-trial"
                className="group inline-flex items-center justify-between gap-3 w-full h-14 px-7 rounded-full font-medium text-sm transition-all hover:scale-[1.01] shadow-[0_14px_28px_-12px_rgba(255,77,109,0.55)]"
                style={{ background: POP.coral, color: "#fff" }}
              >
                Book a 10-Min Demo
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <Link to="/login" data-testid="final-cta-signin">
                <button
                  className="inline-flex items-center justify-between gap-3 w-full h-14 px-7 rounded-full font-medium text-sm border transition-colors"
                  style={{ borderColor: "rgba(15,18,16,0.16)", color: SLATE }}
                >
                  Sign in
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              </Link>
              <div className="text-xs mt-2" style={{ color: MUTED }}>
                No card required. Live in 10 minutes.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="px-8 py-12 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex items-center gap-3">
          <CurateMark />
          <span className="font-display font-bold tracking-tight">Getcurate</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#1B1340]/45">getcurate.shop</span>
        </div>
        <div className="flex items-center gap-8 text-xs text-[#1B1340]/50">
          <a href="#verticals" className="hover:text-[#1B1340]/85">Use Cases</a>
          <a href="#integrations" className="hover:text-[#1B1340]/85">Integrations</a>
          <a href="#pricing" className="hover:text-[#1B1340]/85">Pricing</a>
          <Link to="/login" className="hover:text-[#1B1340]/85">Sign in</Link>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#1B1340]/40">
          © 2026 Getcurate · Built for the shop floor
        </div>
      </div>
    </footer>
  );
}
